import * as enum_ from "lib/enum"
import * as log from "lib/log"

interface result<T, E = error> {
	Ok: T | undefined
	Err: E
}

// Create a Member type for Optional values
export class Result<T, E = Error> extends enum_.Variant<result<T, E>> {
	/* eslint-disable */
	//@ts-ignore
	private match<R>(handlers: { Ok: (value: T) => R; Err: (err: E) => R | never }): R {
		return super.Match(handlers as any)
	}

	/* eslint-disable */
	//@ts-ignore
	override Match<R>(handlers: { Ok: (value: T) => R; Err: (err: E) => R }): R {
		return this.match(handlers)
	}

	static Ok<T, E = Error>(value?: T): Result<T, E> {
		return new Result<T, E>("Ok", value)
	}

	static Err<T, E = Error>(error: E): Result<T, E> {
		return new Result<T, E>("Err", error)
	}

	Unwrap(): T {
		return this.match({
			Ok: (val) => val,
			Err: (err) => log.Fatalf("Called unwrap on Err value:, %j", err),
		})
	}

	UnwrapOr(defaultValue: T | null): T {
		return this.match({
			Ok: (val) => val,
			Err: () => defaultValue as T,
		})
	}

	AndThen<U>(fn: (value: T) => Result<U, E>): Result<U, E> {
		return this.match({
			Ok: fn,
			Err: Result.Err<U, E>,
		})
	}

	IfErr<U>(cb: (e: E) => U): Opt<NonNullable<U>> {
		return this.IfLet().Err(cb)
	}

	Copy<R extends Record<string, any>>(this: R): R {
		if (this instanceof Result) {
			return this.match({
				Ok: (v) => Result.Ok(structuredClone(v)),
				Err: (e) => Result.Err(e),
			}) as unknown as R
		}
		return structuredClone(this)
	}

	OrElse(fn: (err: E) => Result<T, E>): Result<T, E> {
		return this.match({
			Ok: Result.Ok,
			Err: fn,
		})
	}

	IsOk(): bool {
		return this.Is("Ok")
	}

	IsErr(): bool {
		return this.Is("Err")
	}

	UnwrapOrElse(fn: (err: E) => T): T {
		return this.match({
			Ok: (val) => val,
			Err: fn,
		})
	}

	Map<U>(fn: (val: T) => U): Result<U, E> {
		return this.match({
			Ok: (val) => Result.Ok<U, E>(fn(val)),
			Err: Result.Err<U, E>,
		})
	}

	MapTo<U>(val: U): Result<U, E> {
		return this.match({
			Ok: () => Result.Ok<U, E>(val),
			Err: Result.Err<U, E>,
		})
	}
	UnwrapErr(): E {
		return this.match({
			Ok: () => log.Fatal("Called unwrapErr on Ok value"),
			Err: (err) => err,
		})
	}
	MapErr<F>(fn: (err: E) => F): Result<T, F> {
		return this.match({
			Ok: Result.Ok<T, F>,
			Err: (err) => Result.Err(fn(err)),
		})
	}
	Expect(message: string): T {
		return this.match({
			Ok: (val) => val,
			Err: (err) => log.Fatalf("Called expect on err value: %s: %j", message, err),
		})
	}
}

// Factory functions
export const Ok = Result.Ok
export const Err = Result.Err
